import { useState } from "react";
import { Formik } from "formik";
import {
  useWhmSnackbar,
  WhmBox,
  WhmDropzone,
  whmTheme,
} from "@securitize/reactjs-whm";
import { UserInfo } from "../../../types/UserInfo";
import { InvestorInfo } from "../../../types/investorInformation.interface";
import FormBlock, { FormBlockDescription } from "./FormBlock";
import { WhmButton, WhmLink, WhmStack } from "@securitize/reactjs-whm";
import { ISRLinkResolver } from "./FormTypeSelector";
import { W8BENECertificationType, W8BENEFormType } from "./FormType";
import CertificationInputs from "./Inputs/CertificationInputs";
import FormConfirmationModal from "./FormConfirmationModal";
import { markTaxFormSubmitted } from "../../Dashboard/components/TaxFormSubmittedModal";
import {
  getW8BENEPresignedUrls,
  getW8BENETaxFormExample,
  postW8BENETaxForm,
} from "../../../data/rest/taxForms";
import { uploadFileToS3 } from "../../../helpers/s3.helper";
import { translationKeys } from "../../../contexts/I18nContext";
import useTranslations from "../../../hooks/useTranslations";
import useMixPanel, { TrackingType } from "../../../hooks/useMixPanel";
import { TaxFormType } from "../../../types/documents.interface";
import W8BENEFormSkeleton from "./W8BENEForm.skeleton";
import { useMFEContext } from "../../../contexts/MFEContext";
import useRefreshForm from "../../../hooks/useRefreshForm";
import MissingInformationCallout from "./MissingInformationCallout";

type Props = {
  isLoading?: boolean;
  userInfo: UserInfo;
  investorInfo: InvestorInfo;
};

function W8BENEForm({ isLoading, userInfo: _, investorInfo: __ }: Props) {
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | undefined>();
  const [formData, setFormData] = useState<W8BENEFormType | undefined>();
  const snackbar = useWhmSnackbar();
  const track = useMixPanel();
  const { getTranslation: t } = useTranslations();
  const { onUpdate } = useMFEContext();
  const { refresh } = useRefreshForm();

  if (isLoading) return <W8BENEFormSkeleton />;

  const onSubmit = async (values: W8BENEFormType) => {
    setIsSubmitted(true);
    track(TrackingType.TA_PAYOUTS_W8BEN_E_UPLOAD);
    await postW8BENETaxForm(values);
    markTaxFormSubmitted(TaxFormType.W8BENE);
    snackbar.show(
      t(translationKeys.FORM_FEEDBACK_SUCCESS_DOCUMENT, {
        document: TaxFormType.W8BENE,
      }),
      {
        severity: "success",
      },
    );
    onUpdate?.();
    refresh();
  };

  const onDownloadExample = async () => {
    track(TrackingType.TA_PAYOUTS_W8BEN_E_DOWNLOAD);
    await getW8BENETaxFormExample().then(({ url }) => {
      if (url) {
        const link = document.createElement("a");
        link.target = "_blank";
        link.download = url.split("/").pop() ?? "";
        link.href = url;
        link.click();
      }
    });
  };

  return (
    <>
      <FormBlock
        title={t(translationKeys.FORM_W8BENE_TITLE)}
        description={
          <FormBlockDescription>
            {t(translationKeys.FORM_W8BENE_TEXT)}{" "}
            <WhmLink
              href={ISRLinkResolver[TaxFormType.W8BENE]}
              underline="none"
              variant="body2"
              rel="noopener noreferrer"
              target="_blank"
              sx={{
                textDecoration: "underline !important",
                color: `${whmTheme.palette.primary.main} !important`,
              }}
            >
              {t(translationKeys.FORM_HEADER_SUBTITLE_DOCUMENT_LINK)}
            </WhmLink>
          </FormBlockDescription>
        }
      >
        <WhmStack>
          {!uploadedFile && (
            <MissingInformationCallout
              customText={t(translationKeys.FORM_SECTION_W8BENE_FILE_WARNING)}
            />
          )}
          <WhmBox
            sx={{
              marginTop: "1rem !important",
            }}
          >
            <WhmDropzone
              required={true}
              uploadingErrorText={t(
                translationKeys.FORM_W8BENE_DROPZONE_MAX_SIZE_ERROR_MESSAGE,
              ).replace("$size", "10")}
              title={t(translationKeys.FORM_W8BENE_SECTION_DROPZONE_TITLE)}
              description={t(
                translationKeys.FORM_W8BENE_SECTION_DROPZONE_DESCRIPTION,
              )}
              uploadButtonText={t(
                translationKeys.FORM_W8BENE_SECTION_DROPZONE_BUTTON_UPLOAD,
              )}
              deleteButtonText={t(
                translationKeys.FORM_W8BENE_SECTION_DROPZONE_BUTTON_DELETE,
              )}
              extraActionButtonText={t(
                translationKeys.FORM_W8BENE_SECTION_DROPZONE_BUTTON_DOWNLOAD,
              )}
              uploadingCancelText={t(
                translationKeys.FORM_W8BENE_SECTION_DROPZONE_BUTTON_CANCEL,
                {
                  $fileName: uploadedFile?.name ?? "",
                },
              )}
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onExtraActionClick={onDownloadExample}
              onValidate={(file: File) => {
                // Check if file size exceeds 10MB (10 * 1024 * 1024 bytes)
                if (file.size > 10 * 1024 * 1024) {
                  return {
                    error: t(
                      translationKeys.FORM_W8BENE_DROPZONE_MAX_SIZE_ERROR_MESSAGE,
                    ).replace("$size", "10"),
                  };
                }
                // eslint-disable-next-line consistent-return
                return;
              }}
              onUpload={async (file: File) => {
                setUploadedFile(file);
                const presignedPostData = await getW8BENEPresignedUrls(
                  file.type,
                );
                await uploadFileToS3(presignedPostData, file);
                setFormData({
                  contentType: file.type,
                  fileKey: presignedPostData.fields.Key,
                });
              }}
              onDelete={(file) => {
                setUploadedFile(undefined);
                snackbar.show(
                  t(
                    translationKeys.FORM_W8BENE_DROPZONE_DELETION_MESSAGE,
                  ).replace("$fileName", file?.name ?? "File"),
                  {
                    severity: "success",
                  },
                );
              }}
              onError={() => {
                snackbar.show(t(translationKeys.FORM_FEEDBACK_ERROR), {
                  severity: "error",
                });
                setUploadedFile(undefined);
              }}
            />
          </WhmBox>
        </WhmStack>
      </FormBlock>
      <Formik<W8BENECertificationType>
        initialValues={{ agree: false }}
        onSubmit={() => {}}
      >
        {({ values }) => (
          <>
            <FormBlock
              title={t(translationKeys.FORM_SECTION_CERTIFICATION_TITLE)}
              description={t(
                translationKeys.FORM_SECTION_CERTIFICATION_SUBTITLE_W8BENE,
              )}
            >
              <CertificationInputs
                formType={TaxFormType.W8BENE}
                showSignatureFields={false}
              />
            </FormBlock>
            <WhmButton
              variant="contained"
              color="primary"
              sx={{ width: "fit-content", marginTop: "1rem" }}
              disabled={!Boolean(formData) || !values.agree || isSubmitted}
              onClick={() => setShowConfirmationModal(true)}
            >
              {t(translationKeys.FORM_BUTTONS_SUBMIT)}
            </WhmButton>
          </>
        )}
      </Formik>
      {Boolean(formData) && showConfirmationModal && (
        <FormConfirmationModal
          open={showConfirmationModal}
          onConfirm={() => onSubmit(formData as W8BENEFormType)}
          onClose={() => setShowConfirmationModal(false)}
          onCancel={() => setShowConfirmationModal(false)}
          onError={() => {
            setShowConfirmationModal(false);
            setIsSubmitted(false);
            snackbar.show(t(translationKeys.FORM_FEEDBACK_ERROR), {
              severity: "error",
            });
          }}
        />
      )}
    </>
  );
}

export default W8BENEForm;
